import { Link } from "react-router-dom";
import { z } from "zod";
import { Field, useForm, SuccessPanel, SubmitRow, Honeypot, Consent, consentRule } from "@/components/forms/FormKit";

const STAGES = ["Idea", "Validating", "Prototype", "MVP / early users", "Revenue"];
const TYPES = ["Technical co-building", "Product validation and prototyping", "Engineering partnership", "Cash-plus-equity arrangement", "Long-term venture collaboration", "Not sure yet"];

const schema = z.object({
  founder_name: z.string().trim().min(2, "Please enter your name."),
  email: z.string().trim().email("Please enter a valid work email."),
  project_name: z.string().trim().min(1, "Please name your company or project."),
  idea: z.string().trim().min(20, "Please describe the idea (at least 20 characters)."),
  problem: z.string().trim().min(10, "Please describe the problem (at least 10 characters)."),
  target_users: z.string().trim().min(3, "Who are the target users?"),
  stage: z.enum(STAGES, { errorMap: () => ({ message: "Please select your current stage." }) }),
  team: z.string().trim().min(2, "Tell us about the existing team."),
  partnership_type: z.enum(TYPES, { errorMap: () => ({ message: "Please select a partnership type." }) }),
  links: z.string().max(1000).optional(),
  consent: consentRule,
});

export default function PartnershipForm() {
  const f = useForm({
    initial: { founder_name: "", email: "", project_name: "", idea: "", problem: "", target_users: "", stage: "", team: "", partnership_type: "", links: "", consent: false, website: "" },
    schema, endpoint: "/partnership", kind: "partnership_application",
  });
  if (f.state === "success") {
    return <SuccessPanel testId="partnership-success" title="Application received." text="Thank you for sharing your idea. We review every application carefully and will reply by email. Partnerships are selective, so please don't treat this as a commitment either way." />;
  }
  const sel = (name, opts) => (
    <select className="field appearance-none" {...f.bind(name)}>
      <option value="" disabled>Select one</option>
      {opts.map((o) => <option key={o} value={o} className="bg-si-bg">{o}</option>)}
    </select>
  );
  return (
    <form onSubmit={f.submit} noValidate className="relative space-y-6" data-testid="partnership-form">
      <Honeypot bind={f.bind} />
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Founder name" name="founder_name" error={f.errors.founder_name}><input className="field" autoComplete="name" {...f.bind("founder_name")} /></Field>
        <Field label="Work email" name="email" error={f.errors.email}><input type="email" className="field" autoComplete="email" {...f.bind("email")} /></Field>
      </div>
      <Field label="Company or project name" name="project_name" error={f.errors.project_name}><input className="field" {...f.bind("project_name")} /></Field>
      <Field label="Product idea" name="idea" error={f.errors.idea} hint="A high-level description is enough — please don't include confidential trade secrets.">
        <textarea rows={4} className="field resize-y" {...f.bind("idea")} />
      </Field>
      <Field label="Problem being solved" name="problem" error={f.errors.problem}><textarea rows={3} className="field resize-y" {...f.bind("problem")} /></Field>
      <Field label="Target users" name="target_users" error={f.errors.target_users}><input className="field" {...f.bind("target_users")} /></Field>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Current stage" name="stage" error={f.errors.stage}>{sel("stage", STAGES)}</Field>
        <Field label="Partnership sought" name="partnership_type" error={f.errors.partnership_type}>{sel("partnership_type", TYPES)}</Field>
      </div>
      <Field label="Existing team" name="team" error={f.errors.team} hint="Who's involved today and what roles do they cover?"><textarea rows={3} className="field resize-y" {...f.bind("team")} /></Field>
      <Field label="Relevant links" name="links" optional hint="Website, deck, demo, or a shareable document link (supporting documents can be shared as a link).">
        <textarea rows={2} className="field resize-y" {...f.bind("links")} />
      </Field>
      <Consent bind={f.bind} error={f.errors.consent}>
        I agree that SparkIdea Interactive may store and review this application to evaluate a potential partnership, as described in the <Link to="/privacy" className="text-si-cyan underline underline-offset-4">Privacy Policy</Link>. I understand partnerships are selective and not guaranteed.
      </Consent>
      <SubmitRow state={f.state} serverError={f.serverError} label="Submit Application" testId="partnership-submit" />
    </form>
  );
}
